import { PageHeader } from "@/components/ui/page";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
	Plus,
	Search,
	Monitor,
	Edit,
	Trash2,
	Eye,
	ExternalLink,
	LayoutGrid,
	XCircle,
	ArrowUpDown,
	Users,
	Package,
	Wallet,
	CalendarClock,
	User as UserIcon,
	PlayCircle,
	History,
} from "lucide-react";
import { posAPI, branchesAPI, shiftsAPI, usersAPI } from "@/lib/api";
import { Pos, PosStatus, Branch } from "@/types";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { usePermission } from "@/hooks/usePermission";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { SUPER_ADMIN_USER_ID } from "@/lib/constants/configs";
import { useToast } from "@/hooks/use-toast";
import { getBestBadgeVariant, formatDate, formatNumber, cn } from "@/lib/utils";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { Loading } from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { confirmAction } from "@/components/ui/confirm-dialog";

interface Shift {
	id: number;
	status: string;
	openingCash: number;
	closingCash?: number;
	expectedCash?: number;
	variance?: number;
	openedAt: string;
	closedAt?: string;
	branchId: number;
	posId?: number;
	branch?: { name: string };
	pos?: { name: string };
	openedBy?: { firstName: string; lastName: string };
	closedBy?: { firstName: string; lastName: string };
}

interface ShiftCashMovement {
	id: number;
	type: "in" | "out";
	amount: number | string;
	reason?: string | null;
	createdAt: string;
	user?: { firstName?: string; lastName?: string };
}

const getVarianceClassName = (variance?: number) => {
	if (variance == null) return "text-muted-foreground";
	if (Number(variance) < 0) return "text-destructive";
	if (Number(variance) > 0) return "text-success";
	return "text-muted-foreground";
};

/**
 * Points of sale merged with shifts: a shift is a POS's working session,
 * so each POS card shows its live shift state with open/close/drawer
 * actions in place, and the shifts log sits below on the same page.
 */
const PosListPage: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { toast } = useToast();
	const { user } = useAuth();
	const currency = useCurrency();
	const canManageShifts = usePermission("pos.update");

	const [posList, setPosList] = useState<Pos[]>([]);
	const [branches, setBranches] = useState<Branch[]>([]);
	const [openShiftsByPos, setOpenShiftsByPos] = useState<Map<number, Shift>>(new Map());
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Filters (shared by the board and the shifts log)
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedBranch, setSelectedBranch] = useState<string>("all");
	const [selectedStatus, setSelectedStatus] = useState<PosStatus | "all">("all");

	// Board pagination
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalPos, setTotalPos] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(12);

	// Per-POS shifts log dialog
	const [logPos, setLogPos] = useState<Pos | null>(null);
	const [logShifts, setLogShifts] = useState<Shift[]>([]);
	const [logLoading, setLogLoading] = useState(false);

	// Open-shift dialog (branch + pos preset from the card)
	const [openShiftPos, setOpenShiftPos] = useState<Pos | null>(null);
	const [openingCash, setOpeningCash] = useState(0);
	const [openedById, setOpenedById] = useState<number | "">("");
	const [branchEmployees, setBranchEmployees] = useState<{ id: number; firstName: string; lastName: string }[]>([]);
	const [loadingEmployees, setLoadingEmployees] = useState(false);

	// Close-shift dialog with preview
	const [closeDialog, setCloseDialog] = useState<Shift | null>(null);
	const [closingCash, setClosingCash] = useState(0);
	const [closePreview, setClosePreview] = useState<{
		networkTotal: number;
		cashSalesTotal: number;
		otherSalesTotal: number;
		expectedDrawerCash: number;
		openingCash: number;
		cashInTotal: number;
		cashOutTotal: number;
		drawerNetAdjustment: number;
	} | null>(null);
	const [loadingClosePreview, setLoadingClosePreview] = useState(false);

	// Drawer movements dialog
	const [movementDialogShift, setMovementDialogShift] = useState<Shift | null>(null);
	const [cashMovements, setCashMovements] = useState<ShiftCashMovement[]>([]);
	const [loadingCashMovements, setLoadingCashMovements] = useState(false);

	const branchFilterId = selectedBranch !== "all" ? Number(selectedBranch) : undefined;

	const fetchPosList = useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);
			const response = await posAPI.getAll({
				page: currentPage,
				limit: itemsPerPage,
				search: searchTerm || undefined,
				branchId: branchFilterId,
				status: selectedStatus !== "all" ? selectedStatus : undefined,
			});
			setPosList(response.pos || []);
			setTotalPos(response.total || 0);
			setTotalPages(Math.ceil((response.total || 0) / itemsPerPage));
		} catch (err) {
			setError("Failed to fetch POS list");
			console.error("POS fetch error:", err);
		} finally {
			setIsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on list inputs
	}, [currentPage, itemsPerPage, searchTerm, selectedBranch, selectedStatus]);

	/** Open shifts power the live state on the POS cards. */
	const fetchOpenShifts = useCallback(async () => {
		try {
			const data = await shiftsAPI.getAll({ page: 1, limit: 100 });
			const map = new Map<number, Shift>();
			(data?.shifts ?? []).forEach((shift: Shift) => {
				if (shift.status === "open" && shift.posId != null) map.set(shift.posId, shift);
			});
			setOpenShiftsByPos(map);
		} catch {
			setOpenShiftsByPos(new Map());
		}
	}, []);

	const fetchPosLog = useCallback(async (pos: Pos) => {
		try {
			setLogLoading(true);
			const data = await shiftsAPI.getAll({ page: 1, limit: 30, posId: Number(pos.id) });
			setLogShifts(data?.shifts ?? []);
		} catch {
			setLogShifts([]);
		} finally {
			setLogLoading(false);
		}
	}, []);

	const openPosLog = (pos: Pos) => {
		setLogPos(pos);
		setLogShifts([]);
		void fetchPosLog(pos);
	};

	useEffect(() => {
		fetchPosList();
	}, [fetchPosList]);

	useEffect(() => {
		fetchOpenShifts();
	}, [fetchOpenShifts]);

	useEffect(() => {
		branchesAPI
			.getAll()
			.then((response: any) => setBranches(Array.isArray(response) ? response : (response?.data ?? [])))
			.catch(() => setBranches([]));
	}, []);

	const refreshAfterShiftChange = () => {
		fetchOpenShifts();
		if (logPos) void fetchPosLog(logPos);
	};

	/* ===== open shift (branch/pos come from the card) ===== */

	useEffect(() => {
		if (!openShiftPos || user?.id !== SUPER_ADMIN_USER_ID || !openShiftPos.branchId) {
			setBranchEmployees([]);
			setOpenedById("");
			return;
		}
		let cancelled = false;
		setLoadingEmployees(true);
		usersAPI
			.getAll({ branchId: openShiftPos.branchId, limit: 500 })
			.then((data: any) => {
				if (cancelled) return;
				const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
				setBranchEmployees(
					list.map((u: any) => ({ id: u.id, firstName: u.firstName ?? "", lastName: u.lastName ?? "" })),
				);
				setOpenedById("");
			})
			.catch(() => {
				if (!cancelled) setBranchEmployees([]);
			})
			.finally(() => {
				if (!cancelled) setLoadingEmployees(false);
			});
		return () => {
			cancelled = true;
		};
	}, [openShiftPos, user?.id]);

	// Guards the open/close shift dialogs against double submission.
	const [shiftSubmitting, setShiftSubmitting] = useState(false);

	const handleOpenShift = async () => {
		if (!openShiftPos?.branchId || shiftSubmitting) return;
		const payload: { branchId: number; posId: number; openingCash: number; openedById?: number } = {
			branchId: Number(openShiftPos.branchId),
			posId: Number(openShiftPos.id),
			openingCash: Number(openingCash) || 0,
		};
		if (user?.id === SUPER_ADMIN_USER_ID && openedById !== "" && typeof openedById === "number") {
			payload.openedById = openedById;
		}
		setShiftSubmitting(true);
		try {
			await shiftsAPI.open(payload);
			toast({ title: t("common.success"), description: t("shifts.opened", "Shift opened") });
			setOpenShiftPos(null);
			setOpeningCash(0);
			setOpenedById("");
			refreshAfterShiftChange();
		} catch (err: any) {
			const msg = err?.response?.data?.message;
			toast({
				title: t("common.error"),
				description: msg ? t(msg) : t("shifts.openFailed", "Failed to open shift"),
				variant: "destructive",
			});
		} finally {
			setShiftSubmitting(false);
		}
	};

	/* ===== close shift (with drawer preview) ===== */

	useEffect(() => {
		if (!closeDialog) {
			setClosePreview(null);
			return;
		}
		let cancelled = false;
		setLoadingClosePreview(true);
		shiftsAPI
			.getClosePreview(closeDialog.id)
			.then(data => {
				if (!cancelled) setClosePreview(data);
			})
			.catch(() => {
				if (!cancelled) setClosePreview(null);
			})
			.finally(() => {
				if (!cancelled) setLoadingClosePreview(false);
			});
		return () => {
			cancelled = true;
		};
	}, [closeDialog]);

	const handleCloseShift = async () => {
		if (!closeDialog || shiftSubmitting) return;
		setShiftSubmitting(true);
		try {
			const manual = Number(closingCash) || 0;
			await shiftsAPI.close(closeDialog.id, { manualCashCounted: manual, closingCash: manual });
			toast({ title: t("common.success"), description: t("shifts.closed", "Shift closed") });
			setCloseDialog(null);
			setClosingCash(0);
			refreshAfterShiftChange();
		} catch (err: any) {
			const msg = err?.response?.data?.message;
			toast({
				title: t("common.error"),
				description: msg ? t(msg) : t("shifts.closeFailed", "Failed to close shift"),
				variant: "destructive",
			});
		} finally {
			setShiftSubmitting(false);
		}
	};

	const openCashMovements = async (shift: Shift) => {
		setMovementDialogShift(shift);
		setCashMovements([]);
		setLoadingCashMovements(true);
		try {
			const data = await shiftsAPI.getCashMovements(shift.id);
			setCashMovements(Array.isArray(data) ? data : []);
		} catch {
			setCashMovements([]);
			toast({
				title: t("common.error"),
				description: t("shifts.drawer.historyLoadFailed", "Failed to load drawer movements"),
				variant: "destructive",
			});
		} finally {
			setLoadingCashMovements(false);
		}
	};

	const handleDeletePos = async (posId: number | string) => {
		if (await confirmAction(t("pos.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				await posAPI.delete(posId);
				toast({ title: t("common.success"), description: t("pos.messages.deleted") });
				fetchPosList();
			} catch (err) {
				toast({
					title: t("common.error"),
					description: t("pos.errors.deleteFailed"),
					variant: "destructive",
				});
			}
		}
	};

	if (isLoading && currentPage === 1 && posList.length === 0 && !error) {
		return <Loading />;
	}

	return (
		<div className="space-y-5 p-4 md:p-6">
			<PageHeader
				title={t("navigation.pos")}
				actions={
					<PermissionGate permission="pos.create">
						<Button className="flex items-center gap-2 shrink-0" onClick={() => navigate("/pos/new")}>
							<Plus className="h-4 w-4" />
							{t("pos.actions.add")}
						</Button>
					</PermissionGate>
				}
			/>
			{/* Counters + add */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex flex-wrap gap-3">
					<div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-2.5">
						<Monitor className="h-5 w-5 text-muted-foreground" />
						<div>
							<div className="text-lg font-bold leading-none">{totalPos}</div>
							<div className="text-xs text-muted-foreground mt-1">{t("posHub.totalPos")}</div>
						</div>
					</div>
					<div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-2.5">
						<span className="relative flex h-3 w-3">
							<span className="relative inline-flex h-3 w-3 rounded-full bg-success" />
						</span>
						<div>
							<div className="text-lg font-bold leading-none">{openShiftsByPos.size}</div>
							<div className="text-xs text-muted-foreground mt-1">{t("posHub.openShiftsNow")}</div>
						</div>
					</div>
				</div>
			</div>

			{/* Filters */}
			<div className="filter-bar flex flex-col gap-3 rounded-2xl border border-border/60 bg-card p-4 sm:flex-row sm:items-center">
				<div className="relative flex-1 sm:max-w-sm">
					<Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
					<Input
						placeholder={t("pos.filters.searchPlaceholder")}
						value={searchTerm}
						onChange={e => {
							setSearchTerm(e.target.value);
							setCurrentPage(1);
						}}
						className="ps-10"
					/>
				</div>
				<select
					aria-label={t("pos.form.branch")}
					value={selectedBranch}
					onChange={e => {
						setSelectedBranch(e.target.value);
						setCurrentPage(1);
					}}
					className="flex h-12 w-full sm:w-48 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
				>
					<option value="all">{t("pos.filters.allBranches")}</option>
					{branches.map(branch => (
						<option key={branch.id} value={String(branch.id)}>
							{branch.name}
						</option>
					))}
				</select>
				<select
					aria-label={t("pos.form.status")}
					value={selectedStatus}
					onChange={e => {
						setSelectedStatus(e.target.value as PosStatus | "all");
						setCurrentPage(1);
					}}
					className="flex h-12 w-full sm:w-44 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
				>
					<option value="all">{t("pos.filters.allStatuses")}</option>
					<option value={PosStatus.ACTIVE}>{t("common.active")}</option>
					<option value={PosStatus.INACTIVE}>{t("common.inactive")}</option>
				</select>
				{(searchTerm || selectedBranch !== "all" || selectedStatus !== "all") && (
					<Button
						variant="link"
						className="text-destructive"
						onClick={() => {
							setSearchTerm("");
							setSelectedBranch("all");
							setSelectedStatus("all");
							setCurrentPage(1);
						}}
					>
						{t("pos.filters.clearFilters")}
					</Button>
				)}
			</div>

			{/* Live POS board */}
			{posList.length === 0 && !error ? (
				<Card>
					<CardContent className="pt-6">
						<div className="text-center py-12">
							<Monitor className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
							<h3 className="text-lg font-semibold mb-2">{t("pos.empty.title")}</h3>
							<p className="text-muted-foreground mb-4">{t("pos.empty.description")}</p>
						</div>
					</CardContent>
				</Card>
			) : (
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
					{posList.map(pos => {
						const openShift = openShiftsByPos.get(Number(pos.id));
						const isInactive = pos.status !== PosStatus.ACTIVE;
						return (
							<Card
								key={pos.id}
								className={cn(
									"border-t-2 transition-shadow hover:shadow-md",
									openShift ? "border-t-emerald-500" : "border-t-border",
									isInactive && "opacity-70",
								)}
							>
								<CardContent className="space-y-3 pt-5">
									{/* Identity + actions */}
									<div className="flex items-start justify-between gap-2">
										<div className="min-w-0">
											<div className="flex flex-wrap items-center gap-2">
												<span className="truncate text-base font-bold">{pos.name}</span>
												{pos.isMenuWeb && (
													<Badge variant="secondary" className="text-xs font-normal">
														{t("pos.table.menuWeb")}
													</Badge>
												)}
												{pos.offersTableService && (
													<Badge
														variant="outline"
														className="gap-1 border-success/30 bg-success/10 text-xs font-normal text-success"
													>
														<LayoutGrid className="h-3 w-3" />
														{t("pos.table.tableService")}
													</Badge>
												)}
												{isInactive && (
													<Badge variant={getBestBadgeVariant("warning")}>
														{t("common.inactive")}
													</Badge>
												)}
											</div>
											<div className="mt-1 text-xs text-muted-foreground">
												{pos.code && <span className="font-mono">{pos.code}</span>}
												{pos.branch && <span> · {pos.branch.name}</span>}
											</div>
										</div>
										<div className="flex shrink-0 items-center gap-1">
											{pos.menuSlug ? (
												<PermissionGate permission="pos.read">
													<Button
														size="sm"
														variant="outline"
														onClick={() =>
															window.open(
																`https://store.cashiero.net/${pos.menuSlug}`,
																"_blank",
															)
														}
														title={t("common.view")}
														aria-label={t("common.view")}
													>
														<ExternalLink className="h-4 w-4" />
													</Button>
												</PermissionGate>
											) : null}
											<PermissionGate permission="pos.read">
												<Button
													size="sm"
													variant="outline"
													onClick={() => navigate(`/pos/${pos.id}`)}
													title={t("common.view")}
													aria-label={t("common.view")}
												>
													<Eye className="h-4 w-4" />
												</Button>
											</PermissionGate>
											<PermissionGate permission="pos.update">
												<Button
													size="sm"
													variant="outline"
													onClick={() => navigate(`/pos/${pos.id}/edit`)}
													title={t("common.edit")}
													aria-label={t("common.edit")}
												>
													<Edit className="h-4 w-4 text-primary" />
												</Button>
											</PermissionGate>
											<PermissionGate permission="pos.delete">
												<Button
													size="sm"
													variant="outline"
													onClick={() => handleDeletePos(pos.id)}
													title={t("common.delete")}
													aria-label={t("common.delete")}
												>
													<Trash2 className="h-4 w-4 text-destructive" />
												</Button>
											</PermissionGate>
										</div>
									</div>

									{/* Mini counters */}
									<div className="flex gap-4 text-xs text-muted-foreground">
										<span className="flex items-center gap-1">
											<Users className="h-3.5 w-3.5" />
											{pos.employees?.length || 0} {t("navigation.employees")}
										</span>
										<span className="flex items-center gap-1">
											<Package className="h-3.5 w-3.5" />
											{pos.products?.length || 0} {t("navigation.products")}
										</span>
									</div>

									{/* Live shift state */}
									{pos.isMenuWeb ? null : openShift ? (
										<div className="space-y-2 rounded-lg border border-success/25 bg-success/10/60 p-3">
											<div className="flex items-center justify-between gap-2">
												<span className="flex items-center gap-2 text-sm font-semibold text-success">
													<span className="relative flex h-2.5 w-2.5">
														<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success" />
													</span>
													{t("posHub.shiftOpen")}
												</span>
												<span className="text-xs text-muted-foreground">#{openShift.id}</span>
											</div>
											<div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
												<span className="flex items-center gap-1">
													<CalendarClock className="h-3.5 w-3.5" />
													{formatDate(openShift.openedAt)}
												</span>
												<span className="flex items-center gap-1">
													<UserIcon className="h-3.5 w-3.5" />
													{openShift.openedBy
														? `${openShift.openedBy.firstName} ${openShift.openedBy.lastName}`
														: "-"}
												</span>
												<span className="flex items-center gap-1">
													<Wallet className="h-3.5 w-3.5" />
													{t("shifts.table.headers.openingCash")}:{" "}
													<Price value={openShift.openingCash} currency={currency} />
												</span>
											</div>
											{canManageShifts && (
												<div className="flex gap-2 pt-1">
													<Button
														variant="destructive"
														size="sm"
														className="flex-1 gap-1 bg-red-500/90 hover:bg-red-500"
														onClick={() => setCloseDialog(openShift)}
													>
														<XCircle className="h-4 w-4" />
														{t("shifts.closeShift", "Close shift")}
													</Button>
													<Button
														variant="outline"
														size="sm"
														className="gap-1"
														onClick={() => void openCashMovements(openShift)}
													>
														<ArrowUpDown className="h-4 w-4" />
														{t("shifts.drawer.title", "Drawer")}
													</Button>
												</div>
											)}
										</div>
									) : (
										<div className="flex items-center justify-between gap-2 rounded-lg border border-dashed p-3">
											<span className="text-sm text-muted-foreground">
												{t("posHub.noOpenShift")}
											</span>
											{canManageShifts && !isInactive && pos.branchId ? (
												<Button
													size="sm"
													className="gap-1"
													onClick={() => {
														setOpeningCash(0);
														setOpenShiftPos(pos);
													}}
												>
													<PlayCircle className="h-4 w-4" />
													{t("shifts.openShift", "Open shift")}
												</Button>
											) : null}
										</div>
									)}

									{!pos.isMenuWeb && (
										<Button
											variant="ghost"
											size="sm"
											className="w-full gap-2 text-muted-foreground"
											onClick={() => openPosLog(pos)}
										>
											<History className="h-4 w-4" />
											{t("posHub.shiftsLog")}
										</Button>
									)}
								</CardContent>
							</Card>
						);
					})}
				</div>
			)}

			{totalPos > itemsPerPage && (
				<Pagination
					currentPage={currentPage}
					totalPages={totalPages}
					totalItems={totalPos}
					itemsPerPage={itemsPerPage}
					onPageChange={setCurrentPage}
					onPageSizeChange={setItemsPerPage}
					itemName="pos.pagination.pos"
				/>
			)}

			{/* Error state */}
			{error && (
				<Card className="border-destructive">
					<CardContent className="pt-6">
						<div className="text-center">
							<h3 className="text-lg font-semibold mb-2">{t("pos.errors.loadingError")}</h3>
							<p className="text-muted-foreground mb-4">{error}</p>
							<Button onClick={fetchPosList}>{t("common.tryAgain")}</Button>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Per-POS shifts log dialog */}
			<Dialog
				open={!!logPos}
				onOpenChange={open => {
					if (!open) {
						setLogPos(null);
						setLogShifts([]);
					}
				}}
			>
				<DialogContent className="sm:max-w-2xl">
					<DialogHeader>
						<DialogTitle>
							{t("posHub.shiftsLog")}
							{logPos ? ` — ${logPos.name}` : ""}
						</DialogTitle>
					</DialogHeader>
					{logLoading ? (
						<div className="py-8">
							<Loading />
						</div>
					) : logShifts.length === 0 ? (
						<p className="py-8 text-center text-sm text-muted-foreground">
							{t("shifts.noShifts", "No shifts found.")}
						</p>
					) : (
						<div className="max-h-[65vh] divide-y overflow-y-auto rounded-lg border">
							{logShifts.map(shift => (
								<div key={shift.id} className="space-y-2 p-3">
									<div className="flex flex-wrap items-center justify-between gap-2">
										<div className="flex items-center gap-2">
											<span className="font-bold">#{shift.id}</span>
											<Badge variant={shift.status === "open" ? "success" : "secondary"}>
												{t(`shifts.statuses.${shift.status}`, shift.status)}
											</Badge>
											<span className="text-xs text-muted-foreground">
												{shift.openedBy
													? `${shift.openedBy.firstName} ${shift.openedBy.lastName}`
													: "-"}
											</span>
										</div>
										<div className="flex items-center gap-1">
											{shift.status === "open" && canManageShifts ? (
												<Button
													variant="destructive"
													size="sm"
													className="gap-1 bg-red-500/90 hover:bg-red-500"
													onClick={() => setCloseDialog(shift)}
												>
													<XCircle className="h-4 w-4" />
													{t("shifts.closeShift", "Close shift")}
												</Button>
											) : null}
											<Button
												variant="outline"
												size="sm"
												className="gap-1"
												onClick={() => void openCashMovements(shift)}
											>
												<ArrowUpDown className="h-4 w-4" />
												{t("shifts.drawer.title", "Drawer")}
											</Button>
										</div>
									</div>
									<div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground sm:grid-cols-4">
										<span className="flex items-center gap-1">
											<CalendarClock className="h-3.5 w-3.5" />
											{formatDate(shift.openedAt)}
										</span>
										<span className="flex items-center gap-1">
											<CalendarClock className="h-3.5 w-3.5" />
											{shift.closedAt ? formatDate(shift.closedAt) : "-"}
										</span>
										<span>
											{t("shifts.table.headers.openingCash")}:{" "}
											<Price value={shift.openingCash} currency={currency} />
										</span>
										<span className={getVarianceClassName(shift.variance)}>
											{t("shifts.table.headers.variance")}:{" "}
											{shift.variance != null ? (
												<Price value={shift.variance} currency={currency} />
											) : (
												"-"
											)}
										</span>
									</div>
								</div>
							))}
						</div>
					)}
				</DialogContent>
			</Dialog>

			{/* Open-shift dialog (pos preset from card) */}
			<Dialog
				open={!!openShiftPos}
				onOpenChange={open => {
					if (!open) {
						setOpenShiftPos(null);
						setOpenedById("");
						setBranchEmployees([]);
					}
				}}
			>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>{t("shifts.openShift", "Open shift")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
							<div>
								<span className="text-muted-foreground">
									{t("shifts.pointOfSale", "Point of sale")}:{" "}
								</span>
								<span className="font-medium">{openShiftPos?.name}</span>
							</div>
							<div>
								<span className="text-muted-foreground">{t("common.branch", "Branch")}: </span>
								<span>{openShiftPos?.branch?.name || "-"}</span>
							</div>
						</div>
						{user?.id === SUPER_ADMIN_USER_ID && (
							<div className="space-y-2">
								<Label>{t("shifts.openedBy", "Opened by")}</Label>
								{loadingEmployees ? (
									<p className="text-sm text-muted-foreground">{t("common.loading", "Loading...")}</p>
								) : branchEmployees.length === 0 ? (
									<p className="text-sm text-muted-foreground">
										{t("shifts.noEmployeesInBranch", "No employees in this branch.")}
									</p>
								) : (
									<Select
										value={openedById === "" ? "none" : String(openedById)}
										onValueChange={v => setOpenedById(v === "none" ? "" : Number(v))}
									>
										<SelectTrigger aria-label={t("shifts.openedBy", "Opened by")}>
											<SelectValue
												placeholder={t("shifts.openedByPlaceholder", "Select branch employee")}
											/>
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="none">{t("common.select", "Select")}</SelectItem>
											{branchEmployees.map(u => (
												<SelectItem key={u.id} value={String(u.id)}>
													{u.firstName} {u.lastName}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							</div>
						)}
						<div className="space-y-2">
							<Label>{t("shifts.openingCash", "Opening cash")}</Label>
							<Input
								aria-label={t("shifts.openingCash", "Opening cash")}
								type="number"
								min={0}
								step={0.01}
								placeholder={t("shifts.placeholders.openingCash")}
								value={openingCash}
								onChange={e => setOpeningCash(Number(e.target.value) || 0)}
								className="h-12"
							/>
						</div>
					</div>
					<DialogFooter className="gap-2 sm:gap-0">
						<Button variant="outline" onClick={() => setOpenShiftPos(null)}>
							{t("common.cancel", "Cancel")}
						</Button>
						<Button onClick={handleOpenShift} disabled={shiftSubmitting}>
							{t("shifts.open")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Close-shift dialog with drawer preview */}
			<Dialog open={!!closeDialog} onOpenChange={open => !open && setCloseDialog(null)}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{t("shifts.closeShift", "Close shift")}
							{closeDialog?.pos?.name ? ` — ${closeDialog.pos.name}` : ""}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						{loadingClosePreview ? (
							<p className="text-sm text-muted-foreground">{t("common.loading")}</p>
						) : closePreview ? (
							<div className="rounded-lg border border-border/60 bg-muted/30 p-4 space-y-2 text-sm">
								<div className="flex justify-between gap-4">
									<span className="text-muted-foreground">{t("shifts.summary.openingCash")}</span>
									<span className="font-medium tabular-nums">
										{formatNumber(closePreview.openingCash)}
									</span>
								</div>
								<div className="flex justify-between gap-4">
									<span className="text-muted-foreground">{t("shifts.summary.networkTotal")}</span>
									<span className="font-medium tabular-nums">
										{formatNumber(closePreview.networkTotal)}
									</span>
								</div>
								<div className="flex justify-between gap-4">
									<span className="text-muted-foreground">{t("shifts.summary.cashSalesTotal")}</span>
									<span className="font-medium tabular-nums">
										{formatNumber(closePreview.cashSalesTotal)}
									</span>
								</div>
								<div className="flex justify-between gap-4">
									<span className="text-muted-foreground">{t("shifts.summary.otherSalesTotal")}</span>
									<span className="font-medium tabular-nums">
										{formatNumber(closePreview.otherSalesTotal)}
									</span>
								</div>
								<div className="flex justify-between gap-4">
									<span className="text-muted-foreground">{t("shifts.summary.cashInTotal")}</span>
									<span className="font-medium tabular-nums text-success">
										+{formatNumber(closePreview.cashInTotal)}
									</span>
								</div>
								<div className="flex justify-between gap-4">
									<span className="text-muted-foreground">{t("shifts.summary.cashOutTotal")}</span>
									<span className="font-medium tabular-nums text-destructive">
										−{formatNumber(closePreview.cashOutTotal)}
									</span>
								</div>
								<div className="flex justify-between gap-4 pt-2 border-t border-border/50">
									<span className="text-muted-foreground">{t("shifts.summary.expectedDrawer")}</span>
									<span className="font-semibold tabular-nums">
										{formatNumber(closePreview.expectedDrawerCash)}
									</span>
								</div>
								<div className="flex justify-between gap-4 text-base pt-1">
									<span>{t("shifts.summary.closingBalanceReported")}</span>
									<span className="font-bold tabular-nums text-primary">
										{formatNumber(closePreview.networkTotal + (Number(closingCash) || 0))}
									</span>
								</div>
							</div>
						) : null}
						<div className="space-y-2">
							<Label>{t("shifts.closingCash", "Counted cash (drawer)")}</Label>
							<Input
								aria-label={t("shifts.closingCash", "Counted cash (drawer)")}
								type="number"
								min={0}
								step={0.01}
								placeholder={t("shifts.placeholders.closingCash")}
								value={closingCash}
								onChange={e => setClosingCash(Number(e.target.value) || 0)}
								className="h-12"
							/>
						</div>
					</div>
					<DialogFooter className="gap-2 sm:gap-0">
						<Button variant="outline" onClick={() => setCloseDialog(null)}>
							{t("common.cancel", "Cancel")}
						</Button>
						<Button onClick={handleCloseShift} disabled={shiftSubmitting}>
							{t("shifts.close")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Drawer movements dialog */}
			<Dialog
				open={!!movementDialogShift}
				onOpenChange={open => {
					if (!open) {
						setMovementDialogShift(null);
						setCashMovements([]);
					}
				}}
			>
				<DialogContent className="sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>
							{t("shifts.drawer.title", "Drawer movements")} #{movementDialogShift?.id}
						</DialogTitle>
					</DialogHeader>
					{loadingCashMovements ? (
						<div className="py-8">
							<Loading />
						</div>
					) : cashMovements.length === 0 ? (
						<p className="py-8 text-center text-sm text-muted-foreground">
							{t("shifts.drawer.empty", "No drawer movements for this shift.")}
						</p>
					) : (
						<div className="max-h-[60vh] divide-y overflow-y-auto rounded-lg border">
							{cashMovements.map(movement => {
								const cashierName = [movement.user?.firstName, movement.user?.lastName]
									.filter(Boolean)
									.join(" ");
								return (
									<div key={movement.id} className="flex items-start justify-between gap-4 p-3">
										<div className="min-w-0 space-y-1">
											<div className="flex flex-wrap items-center gap-2">
												<Badge variant={movement.type === "in" ? "success" : "destructive"}>
													{movement.type === "in"
														? t("shifts.drawer.cashIn", "Cash in")
														: t("shifts.drawer.cashOut", "Cash out")}
												</Badge>
												<span className="text-xs text-muted-foreground">
													{formatDate(movement.createdAt)}
												</span>
											</div>
											<p className="break-words text-sm">{movement.reason || "—"}</p>
											{cashierName ? (
												<p className="text-xs text-muted-foreground">{cashierName}</p>
											) : null}
										</div>
										<span
											className={cn(
												"shrink-0 font-semibold tabular-nums",
												movement.type === "in" ? "text-success" : "text-destructive",
											)}
										>
											{movement.type === "in" ? "+" : "−"}
											<Price value={movement.amount} currency={currency} />
										</span>
									</div>
								);
							})}
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default PosListPage;
